import React, { useState, useEffect, useMemo } from 'react';
import { Box, Container, Typography, TextField, Button, Card, CardContent, Grid, FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress, Paper, IconButton, CardMedia, Divider, FormControlLabel, Switch } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { RootState } from '../store/store';
import { fetchCategories } from '../services/apiService';
import { buildEventsApiUrl } from '../config/api';
import { getAuthUserId } from '../utils/authUser';
import { buildJsonAuthHeaders } from '../utils/authFetch';

const cashStackImage = '/images/cash-stack.svg';

interface CreateItemForm {
  name: string;
  category: string;
  condition: string;
  notes: string;
  isCashItem: boolean;
  cashValueUSD: string;
}

const conditions = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'];

const formatCashName = (amountText: string) => {
  const amount = Number(amountText);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)} Cash`;
};

const CreateItem: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, initialized } = useSelector((state: RootState) => state.auth);
  const authUserId = getAuthUserId(user as any);
  const [form, setForm] = useState<CreateItemForm>({
    name: '',
    category: '',
    condition: 'Good',
    notes: '',
    isCashItem: false,
    cashValueUSD: '',
  });

  const [categories, setCategories] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const cashItemName = useMemo(() => formatCashName(form.cashValueUSD), [form.cashValueUSD]);

  useEffect(() => {
    if (!initialized) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };

    loadCategories();
  }, [initialized, isAuthenticated, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as HTMLInputElement;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (field: string, value: unknown) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingImage(true);
    setError('');

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('image', file);

        const uploadResponse = await fetch(buildEventsApiUrl('/api/items/temp/upload-image'), {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }

        const uploadData = await uploadResponse.json();
        if (uploadData.success && uploadData.imageUrl) {
          setImages(prev => [...prev, uploadData.imageUrl]);
        }
      }
    } catch (err) {
      setError(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.isCashItem && !form.name.trim()) {
      setError('Item name is required');
      return;
    }

    if (!form.isCashItem && !form.category) {
      setError('Category is required');
      return;
    }

    if (form.isCashItem) {
      const normalizedCashValueUSD = Number(form.cashValueUSD);
      if (!Number.isFinite(normalizedCashValueUSD) || normalizedCashValueUSD <= 0) {
        setError('Cash amount must be greater than zero');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      const saveResponse = await fetch(buildEventsApiUrl('/api/items'), {
        method: 'POST',
        headers: buildJsonAuthHeaders(authUserId),
        body: JSON.stringify({
          userId: authUserId,
          name: form.isCashItem ? cashItemName : form.name.trim(),
          description: '',
          category: form.isCashItem ? 'Cash Offer' : form.category,
          condition: form.isCashItem ? 'Cash' : form.condition,
          notes: form.isCashItem ? '' : form.notes.trim(),
          images: form.isCashItem ? [cashStackImage] : images,
          isCashItem: form.isCashItem,
          cashValueUSD: form.isCashItem ? Number(form.cashValueUSD) : 0,
        }),
      });

      const saveData = await saveResponse.json();

      if (!saveResponse.ok) {
        throw new Error(saveData.error || 'Failed to save item');
      }

      const createdItemId = saveData.item?.id ? String(saveData.item.id) : undefined;

      if (createdItemId && createdItemId !== 'undefined' && createdItemId !== '[object Object]') {
        setSuccessMessage('Item created! Redirecting...');
        setTimeout(() => navigate(`/edit-item/${createdItemId}`), 500);
      } else {
        setSuccessMessage('Item created! Returning to dashboard...');
        setTimeout(() => navigate('/dashboard'), 1500);
      }
    } catch (err: any) {
      console.error('❌ Error creating item:', err);
      setError(err.message || 'Failed to create item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!initialized || !isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/dashboard')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Create New Item
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Add an item to your inventory
            </Typography>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={form.isCashItem}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setForm((prev) => ({
                            ...prev,
                            isCashItem: checked,
                            name: checked ? '' : prev.name,
                            category: checked ? 'Cash Offer' : prev.category,
                            condition: checked ? 'Cash' : (prev.condition === 'Cash' ? 'Good' : prev.condition),
                            notes: checked ? '' : prev.notes,
                          }));
                          if (checked) {
                            setImages([]);
                          }
                        }}
                        disabled={loading}
                      />
                    )}
                    label="This is a cash offer item"
                  />
                </Grid>

                {form.isCashItem && (
                  <Grid item xs={12}>
                    <Alert severity="info">Cash items are auto-named from the amount, use a default cash image, and skip description/notes.</Alert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Item Name"
                    name="name"
                    value={form.isCashItem ? cashItemName : form.name}
                    onChange={handleInputChange}
                    placeholder={form.isCashItem ? 'Auto-generated from amount' : 'e.g., Vintage Camera'}
                    disabled={loading || form.isCashItem}
                    required={!form.isCashItem}
                  />
                </Grid>

                {form.isCashItem && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Cash Amount ($)"
                        name="cashValueUSD"
                        type="number"
                        value={form.cashValueUSD}
                        onChange={handleInputChange}
                        inputProps={{ min: 0.01, step: '0.01' }}
                        disabled={loading}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                        <Box component="img" src={cashStackImage} alt="Cash item" sx={{ width: 96, height: 96, objectFit: 'contain', mb: 1 }} />
                        <Typography variant="body2" color="textSecondary">Default cash item image</Typography>
                      </Paper>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required disabled={loading || form.isCashItem}>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={form.isCashItem ? 'Cash Offer' : form.category}
                      label="Category"
                      onChange={(e) => handleSelectChange('category', e.target.value)}
                    >
                      {(categories.length > 0 ? categories : ['Books', 'Electronics', 'Video Games', 'Sports', 'Clothing', 'Furniture', 'Home & Garden']).map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                      <MenuItem value="Cash Offer">Cash Offer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={loading || form.isCashItem}>
                    <InputLabel>Condition</InputLabel>
                    <Select
                      value={form.isCashItem ? 'Cash' : form.condition}
                      label="Condition"
                      onChange={(e) => handleSelectChange('condition', e.target.value)}
                    >
                      {conditions.map(cond => (
                        <MenuItem key={cond} value={cond}>{cond}</MenuItem>
                      ))}
                      <MenuItem value="Cash">Cash</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {!form.isCashItem && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Notes"
                      name="notes"
                      value={form.notes}
                      onChange={handleInputChange}
                      multiline
                      rows={2}
                      disabled={loading}
                    />
                  </Grid>
                )}

                <Divider sx={{ width: '100%' }} />

                {!form.isCashItem && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageSelect}
                        disabled={loading || uploadingImage}
                        style={{ display: 'none' }}
                        id="image-input"
                      />
                      <label htmlFor="image-input" style={{ width: '100%', display: 'block', cursor: 'pointer' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <AddPhotoAlternateIcon sx={{ fontSize: 40, color: '#999' }} />
                          <Typography variant="body2" color="textSecondary">
                            Click to upload images (optional)
                          </Typography>
                          {uploadingImage && <CircularProgress size={24} />}
                        </Box>
                      </label>
                    </Paper>
                  </Grid>
                )}

                {!form.isCashItem && images.length > 0 && (
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      {images.map((img, idx) => (
                        <Grid item xs={6} sm={4} md={3} key={idx}>
                          <Card>
                            <CardMedia component="img" height="200" image={img} alt={`Item ${idx + 1}`} sx={{ objectFit: 'cover' }} />
                            <CardContent sx={{ p: 1 }}>
                              <IconButton size="small" color="error" onClick={() => removeImage(idx)} disabled={loading || uploadingImage} sx={{ width: '100%' }}>
                                <DeleteIcon /> Remove
                              </IconButton>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}

                <Divider sx={{ width: '100%' }} />

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading || (!form.isCashItem && !form.name.trim()) || (!form.isCashItem && !form.category) || (form.isCashItem && !form.cashValueUSD.trim())}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Create Item'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default CreateItem;
